// src/pages/CircuitSurMesure.jsx
import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  MapPin,
  Users,
  Clock,
  Car,
  Trash,
  PlusCircle,
  Send,
  Mail,
  Phone,
  User,
  Compass,
  Mountain,
  Sparkles,
  CheckCircle,
  Wallet,
  MessageSquare,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import { useAuth } from "../contexts/AuthContext";
import {
  ModernInput,
  ModernSelect,
  ModernDatePicker,
  FormSection
} from "../components/ui/FormComponents";
import zelijPattern from "../assets/zelij3.png";

// ✅ i18n helper (same approach)
import { t as tr } from "../contexts/translations";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://nouzhaback-theta.vercel.app").replace(/\/$/, "");

const getTodayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};

// Inspiration images for the sidebar
const inspirationImages = [
  {
    src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766011/nouzha/destination-tangerrabat-20260317.jpg",
    label: "Casablanca & Rabat",
    desc: "Plages océaniques et histoire",
  },
  {
    src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg",
    label: "Sahara Desert",
    desc: "Nuits sous les étoiles",
  },
  {
    src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg",
    label: "Marrakech",
    desc: "Médina et jardins secrets",
  },
  {
    src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766008/nouzha/destination-chefchaoun-20260317.jpg",
    label: "Chefchaouen",
    desc: "La perle bleue du Rif",
  },
];

const CircuitSurMesure = ({ language }) => {
  const { currentUser } = useAuth();

  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  const today = useMemo(() => getTodayISO(), []);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(0);

  const [email, setEmail] = useState(currentUser?.email || "");
  const [nom, setNom] = useState(currentUser?.nom || "");
  const [prenom, setPrenom] = useState(currentUser?.prenom || "");
  const [telephone, setTelephone] = useState(currentUser?.telephone || "");

  const [circuitInfo, setCircuitInfo] = useState({
    titre: "",
    description: "",
    date_debut: "",
    duree: "",
    nb_personnes: 1,
    type_hebergement: "standard",
    type_vehicule: "standard",
    budget_approx: "",
    demandes_specifiques: "",
  });

  useEffect(() => {
    setEmail(currentUser?.email || "");
    setNom(currentUser?.nom || "");
    setPrenom(currentUser?.prenom || "");
    setTelephone(currentUser?.telephone || "");
  }, [currentUser]);

  const [etapes, setEtapes] = useState([{ ville: "", duree_sejour: 1, activites: "" }]);

  const processCards = [
    {
      title: T("customProcessStep1Title", "Brief voyage"),
      description: T("customProcessStep1Desc", "Partagez vos envies, votre budget et vos dates."),
      icon: <User className="w-5 h-5 text-white" />,
      gradient: "from-orange-500 to-amber-500",
    },
    {
      title: T("customProcessStep2Title", "Conception sur mesure"),
      description: T("customProcessStep2Desc", "Notre équipe construit un itinéraire adapté à votre profil."),
      icon: <Compass className="w-5 h-5 text-white" />,
      gradient: "from-amber-500 to-orange-600",
    },
    {
      title: T("customProcessStep3Title", "Validation rapide"),
      description: T("customProcessStep3Desc", "Vous recevez une proposition claire avec prix et options."),
      icon: <CheckCircle className="w-5 h-5 text-white" />,
      gradient: "from-orange-600 to-red-500",
    },
  ];

  const ajouterEtape = () => {
    setEtapes((prev) => [...prev, { ville: "", duree_sejour: 1, activites: "" }]);
  };

  const supprimerEtape = (index) => {
    setEtapes((prev) => prev.filter((_, i) => i !== index));
  };

  const mettreAJourEtape = (index, champ, valeur) => {
    setEtapes((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [champ]: valeur };
      return copy;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCircuitInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Garde-fou wizard : tant qu'on n'est pas à la dernière étape, on avance
    if (step !== lastStep) { goNext(); return; }
    setLoading(true);

    try {
      const cleanedEtapes = etapes.map((x) => ({
        ville: String(x.ville || "").trim(),
        duree_sejour: Number(x.duree_sejour || 1),
        activites: String(x.activites || "").trim(),
      }));

      if (!circuitInfo.titre.trim()) {
        toast.error(T("vCustomTitle", "Veuillez saisir un titre."));
        setLoading(false);
        return;
      }
      if (!circuitInfo.description.trim()) {
        toast.error(T("vCustomDescription", "Veuillez décrire votre circuit."));
        setLoading(false);
        return;
      }
      if (!circuitInfo.date_debut) {
        toast.error(T("vCustomStartDate", "Veuillez choisir une date de début."));
        setLoading(false);
        return;
      }
      if (!circuitInfo.duree || Number(circuitInfo.duree) <= 0) {
        toast.error(T("vCustomDuration", "Veuillez saisir une durée valide."));
        setLoading(false);
        return;
      }
      if (!email) {
        toast.error(T("vMissingEmail", "Email manquant."));
        setLoading(false);
        return;
      }

      const donnees = {
        ...circuitInfo,
        duree: Number(circuitInfo.duree),
        nb_personnes: Number(circuitInfo.nb_personnes),
        budget_approx: circuitInfo.budget_approx ? Number(circuitInfo.budget_approx) : "",
        etapes: cleanedEtapes,
        email,
        nom,
        prenom,
        telephone,
        nom_utilisateur: `${prenom} ${nom}`.trim() || currentUser?.username || "",
      };

      const response = await axios.post(`${API_BASE}/api/circuits-sur-mesure`, donnees);

      if (response.data?.success) {
        toast.success(T("customCircuitSentOk", "Votre demande de circuit sur mesure a été enregistrée avec succès!"));
        setSuccess(true);

        setCircuitInfo({
          titre: "",
          description: "",
          date_debut: "",
          duree: "",
          nb_personnes: 1,
          type_hebergement: "standard",
          type_vehicule: "standard",
          budget_approx: "",
          demandes_specifiques: "",
        });

        setEtapes([{ ville: "", duree_sejour: 1, activites: "" }]);

        if (!currentUser) {
          setEmail("");
          setNom("");
          setPrenom("");
          setTelephone("");
        }
      } else {
        toast.error(response.data?.message || T("customCircuitServerError", "Erreur lors de l'enregistrement."));
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
      // Affiche TOUTES les erreurs (validation, réseau, serveur) au client
      notifyApiError(
        error,
        T("customCircuitNetworkError", "Une erreur est survenue. Veuillez réessayer plus tard."),
        { title: T("formErrorsTitle", "Veuillez corriger") }
      );
    } finally {
      setLoading(false);
    }
  };

  // ===== Wizard (étapes) =====
  const WIZARD_STEPS = [
    { label: T("wizCircuit", "Votre circuit"), icon: Compass },
    { label: T("wizItinerary", "Itinéraire"), icon: MapPin },
    { label: T("wizContact", "Vos coordonnées"), icon: User },
  ];
  const lastStep = WIZARD_STEPS.length - 1;

  const validateStep = (s) => {
    if (s === 0) {
      if (!circuitInfo.titre.trim()) return T("vTitle", "Veuillez saisir le titre du circuit.");
      if (!circuitInfo.date_debut) return T("vDateStep", "Veuillez choisir la date de début.");
      if (!String(circuitInfo.duree).trim()) return T("vDurationStep", "Veuillez indiquer la durée.");
      if (String(circuitInfo.description).trim().length < 10)
        return T("vDescStep", "La description doit contenir au moins 10 caractères.");
    }
    if (s === 1) {
      if (!etapes.some((e) => e.ville.trim()))
        return T("vStep", "Ajoutez au moins une étape avec une ville.");
    }
    if (s === 2) {
      if (!prenom.trim() || !nom.trim()) return T("vNameStep", "Veuillez saisir votre nom et prénom.");
      if (!email.trim()) return T("vEmailStep", "Veuillez saisir votre email.");
      if (!telephone.trim()) return T("vPhoneStep", "Veuillez saisir votre téléphone.");
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(lastStep, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="w-full min-h-screen relative overflow-hidden" style={{ background: "#FFF7ED" }}>
      {/* Background pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.06,
        }}
      />

      {/* ===== HERO SECTION with stunning image ===== */}
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg"
            alt="Custom Morocco Circuit"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 60%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFF7ED] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 backdrop-blur-md bg-white/20 text-white border border-white/30">
              <Sparkles className="w-4 h-4" />
              {T("customCircuitBadge", "Voyage personnalisé")}
            </div>

            <h1 className="text-4xl md:text-6xl font-serif font-extrabold mb-4 text-white drop-shadow-2xl">
              {T("customCircuitTitle", "Circuit Sur Mesure")}
            </h1>

            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/90 drop-shadow-lg">
              {T(
                "customCircuitSubtitle",
                "Créez votre voyage personnalisé selon vos envies. Nous nous occupons de tout organiser pour vous offrir une expérience unique."
              )}
            </p>
          </motion.div>
        </div>

        {/* Floating mountain icons */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -15, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 4 + i * 1.5,
              repeat: Infinity,
              delay: i * 0.8,
            }}
            className="absolute"
            style={{
              bottom: `${15 + i * 10}%`,
              right: `${5 + i * 12}%`,
            }}
          >
            <Mountain className="w-8 h-8 text-white/30" />
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8" style={{ marginTop: "-2rem" }}>
        {/* ===== PROCESS CARDS with gradient icons ===== */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {processCards.map((item, idx) => (
            <motion.article
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="rounded-2xl bg-white/95 backdrop-blur-sm border border-orange-100 p-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg`}>
                  {item.icon}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-orange-600 font-bold mb-1">
                    {T("customProcessStepLabel", "Étape")} {idx + 1}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{item.description}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </section>

        {/* Trust banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 shadow-sm"
        >
          <p className="text-sm md:text-base text-orange-800 font-medium">
            {T("customTrustBanner", "Paiement sécurisé, accompagnement humain et proposition détaillée avant validation.")}
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-orange-200 text-xs font-bold text-orange-700 w-fit shadow-sm">
            <Clock className="w-3.5 h-3.5" />
            {T("customAvgResponse", "Réponse moyenne: 24h")}
          </div>
        </motion.div>

        {/* ===== MAIN CONTENT: FORM + INSPIRATION SIDEBAR ===== */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* LEFT: Form */}
          <div className="lg:w-2/3">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-8 text-center"
              >
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {T("customCircuitSuccessTitle", "Demande envoyée avec succès!")}
                </h2>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {T("customCircuitSuccessBody", "Nous avons bien reçu votre demande de circuit personnalisé. Notre équipe va l'étudier et vous contactera dans les plus brefs délais.")}
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:from-orange-600 hover:to-red-700 transition-all font-semibold shadow-lg"
                >
                  {T("customCircuitNew", "Créer un autre circuit")}
                </button>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                onSubmit={handleSubmit}
                className="max-w-4xl mx-auto space-y-10"
              >
                {/* ===== Stepper ===== */}
                <div className="flex items-center justify-between gap-2 mb-2 px-2">
                  {WIZARD_STEPS.map((st, i) => {
                    const Icon = st.icon;
                    const done = i < step;
                    const active = i === step;
                    return (
                      <React.Fragment key={st.label}>
                        <div className="flex flex-col items-center text-center">
                          <div
                            className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-all duration-300 ${active
                              ? "bg-orange-600 text-white scale-110 shadow-orange-200"
                              : done
                                ? "bg-green-500 text-white"
                                : "bg-white text-gray-400 border border-gray-200"
                              }`}
                          >
                            {done ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                          </div>
                          <span
                            className={`mt-2 text-[11px] md:text-xs font-bold uppercase tracking-wide ${active ? "text-orange-700" : "text-gray-400"
                              }`}
                          >
                            {st.label}
                          </span>
                        </div>
                        {i < WIZARD_STEPS.length - 1 && (
                          <div
                            className={`h-1 flex-1 rounded-full mb-6 transition-all duration-300 ${i < step ? "bg-green-500" : "bg-gray-200"
                              }`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {step === 0 && (
                <FormSection
                  title={T("customCircuitGeneralInfo", "Informations générales")}
                  icon={Compass}
                  className="z-30"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="md:col-span-2">
                      <ModernInput
                        label={T("customCircuitTripTitle", "Titre de votre circuit")}
                        icon={MapPin}
                        name="titre"
                        value={circuitInfo.titre}
                        onChange={handleChange}
                        placeholder={T("customCircuitTripTitlePh", "Ex: Découverte du Maroc authentique")}
                        required
                      />
                    </div>

                    <ModernSelect
                      label={T("customCircuitPeopleCount", "Nombre de personnes")}
                      icon={Users}
                      name="nb_personnes"
                      value={circuitInfo.nb_personnes}
                      onChange={handleChange}
                      required
                      options={[
                        ...Array.from({ length: 15 }, (_, i) => ({ value: i + 1, label: `${i + 1} personne${i > 0 ? "s" : ""}` })),
                        { value: "16", label: "16+ personnes" }
                      ]}
                    />

                    <ModernDatePicker
                      label={T("customCircuitStartDate", "Date de début souhaitée")}
                      value={circuitInfo.date_debut}
                      min={today}
                      onChange={(e) => setCircuitInfo(prev => ({ ...prev, date_debut: e.target.value }))}
                      language={language}
                      required
                    />

                    <ModernInput
                      label={T("customCircuitTotalDays", "Durée totale (jours)")}
                      icon={Clock}
                      type="number"
                      name="duree"
                      value={circuitInfo.duree}
                      onChange={handleChange}
                      placeholder="Ex: 7"
                      required
                    />

                    <ModernSelect
                      label={T("customCircuitAccommodation", "Hébergement")}
                      icon={Sparkles}
                      name="type_hebergement"
                      value={circuitInfo.type_hebergement}
                      onChange={handleChange}
                      required
                      options={[
                        { value: "standard", label: T("accStandard", "Standard") },
                        { value: "confort", label: T("accComfort", "Confort") },
                        { value: "luxe", label: T("accLuxury", "Luxe") },
                        { value: "riad", label: T("accRiad", "Riad traditionnel") },
                        { value: "mixte", label: T("accMixed", "Mixte") },
                      ]}
                    />

                    <ModernSelect
                      label={T("customCircuitVehicle", "Type de véhicule")}
                      icon={Car}
                      name="type_vehicule"
                      value={circuitInfo.type_vehicule}
                      onChange={handleChange}
                      required
                      options={[
                        { value: "standard", label: T("vehStandard", "Véhicule standard") },
                        { value: "confort", label: T("vehComfort", "Véhicule confort") },
                        { value: "4x4", label: T("veh4x4", "4x4") },
                        { value: "minibus", label: T("vehMinibus", "Minibus") },
                        { value: "luxe", label: T("vehLuxury", "Véhicule de luxe") },
                      ]}
                    />

                    <ModernInput
                      label={T("customCircuitBudget", "Budget / personne (€)")}
                      icon={Wallet}
                      type="number"
                      name="budget_approx"
                      value={circuitInfo.budget_approx}
                      onChange={handleChange}
                      placeholder="Ex: 5000"
                    />

                    <div className="md:col-span-2">
                      <ModernInput
                        label={T("customCircuitDescription", "Description de votre circuit idéal")}
                        value={circuitInfo.description}
                        onChange={(e) => setCircuitInfo(prev => ({ ...prev, description: e.target.value }))}
                        multiline
                        rows={3}
                        placeholder={T("customCircuitDescriptionPh", "Décrivez l'expérience souhaitée...")}
                        required
                      />
                    </div>
                  </div>
                </FormSection>
                )}

                {step === 1 && (
                <FormSection title={T("customCircuitStepsTitle", "Étapes du circuit")} icon={MapPin}>
                  <div className="space-y-6">
                    <div className="flex justify-end mb-4">
                      <button
                        type="button"
                        onClick={ajouterEtape}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl hover:bg-orange-600 transition-all font-black text-xs uppercase tracking-widest shadow-lg"
                      >
                        <PlusCircle className="w-4 h-4" />
                        {T("customCircuitAddStep", "Ajouter une étape")}
                      </button>
                    </div>

                    {etapes.map((etape, index) => (
                      <div key={index} className="p-8 rounded-[2.5rem] bg-orange-50/50 border border-orange-100 relative group overflow-hidden">
                        <div className="absolute top-0 left-0 w-1.5 h-full bg-orange-500 opacity-20 group-hover:opacity-100 transition-opacity" />

                        <div className="flex justify-between items-center mb-8">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-orange-600 flex items-center justify-center text-white font-black shadow-lg">
                              {index + 1}
                            </div>
                            <h3 className="text-sm font-black text-orange-700 uppercase tracking-widest">
                              {T("customCircuitStepLabel", "Étape")}
                            </h3>
                          </div>

                          {etapes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => supprimerEtape(index)}
                              className="w-10 h-10 rounded-2xl bg-white border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow-sm"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          <ModernInput
                            label={T("customCircuitCity", "Ville")}
                            icon={MapPin}
                            value={etape.ville}
                            onChange={(e) => mettreAJourEtape(index, "ville", e.target.value)}
                            placeholder={T("customCircuitCityPh", "Ex: Marrakech")}
                            required
                          />
                          <ModernInput
                            label={T("customCircuitStayDays", "Durée (jours)")}
                            icon={Clock}
                            type="number"
                            value={etape.duree_sejour}
                            onChange={(e) => mettreAJourEtape(index, "duree_sejour", Number(e.target.value))}
                            min="1"
                            required
                          />
                          <div className="md:col-span-2 lg:col-span-1">
                            <ModernInput
                              label={T("customCircuitActivities", "Activités")}
                              icon={Sparkles}
                              value={etape.activites}
                              onChange={(e) => mettreAJourEtape(index, "activites", e.target.value)}
                              placeholder={T("customCircuitActivitiesPh", "Ex: Medina, randonnée...")}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </FormSection>
                )}

                {step === 2 && (
                <>
                <FormSection title={T("customCircuitContactInfo", "Contact")} icon={User}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <ModernInput
                      label={T("firstName", "Prénom")}
                      icon={User}
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      placeholder={T("firstNamePh", "Votre prénom")}
                      required
                    />
                    <ModernInput
                      label={T("lastName", "Nom")}
                      icon={User}
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      placeholder={T("lastNamePh", "Votre nom")}
                      required
                    />
                    <ModernInput
                      label={T("emailLabel", "Email")}
                      icon={Mail}
                      type="email"
                      value={email}
                      onChange={(e) => !currentUser && setEmail(e.target.value)}
                      placeholder={T("customCircuitEmailPh", "Ex: vous@example.com")}
                      readOnly={!!currentUser}
                      required
                    />
                    <ModernInput
                      label={T("phoneNumber", "Téléphone")}
                      icon={Phone}
                      type="tel"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      placeholder="Ex: +212 6..."
                      required
                    />
                  </div>
                </FormSection>

                <div className="mt-8">
                  <ModernInput
                    label={T("customCircuitExtraInfo", "Demandes spécifiques")}
                    icon={MessageSquare}
                    name="demandes_specifiques"
                    value={circuitInfo.demandes_specifiques}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    placeholder={T("customCircuitExtraPh", "Précisez vos besoins...")}
                  />
                </div>
                </>
                )}

                {/* ===== Navigation wizard ===== */}
                <div className="flex items-center justify-between gap-4 pt-2">
                  {step > 0 ? (
                    <button
                      type="button"
                      onClick={goBack}
                      className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-black uppercase tracking-widest text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm transition-all"
                    >
                      ← {T("wizBack", "Précédent")}
                    </button>
                  ) : (
                    <span />
                  )}

                  {step < lastStep ? (
                    <button
                      type="button"
                      onClick={goNext}
                      className="inline-flex items-center gap-2 px-10 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl hover:bg-orange-600 transition-all"
                    >
                      {T("wizNext", "Suivant")} →
                    </button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-3 px-10 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl hover:bg-orange-600 transition-all disabled:opacity-60"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                          {T("sending", "Envoi...")}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {T("submitRequest", "Soumettre ma demande")}
                        </>
                      )}
                    </motion.button>
                  )}
                </div>
              </motion.form>
            )}
          </div>

          {/* RIGHT: Inspiration Sidebar */}
          <div className="lg:w-1/3">
            <div className="sticky top-28 space-y-6">
              {/* Inspiration Gallery */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-amber-100/50"
              >
                <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-500" />
                  {T("customInspirations", "Inspirations")}
                </h3>

                <div className="space-y-4">
                  {inspirationImages.map((img, i) => (
                    <motion.div
                      key={img.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <img
                        src={img.src}
                        alt={img.label}
                        className="w-full h-28 object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute bottom-3 left-3">
                        <p className="text-white font-bold text-sm">{img.label}</p>
                        <p className="text-white/80 text-xs">{img.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Quick Info Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl shadow-xl p-6 text-white"
              >
                <h3 className="font-bold text-lg mb-3">{T("customWhyTailorTitle", "Pourquoi sur mesure ?")}</h3>
                <ul className="space-y-3 text-sm text-white/90">
                  {[
                    T("customWhyTailor1", "Itinéraire 100% personnalisé"),
                    T("customWhyTailor2", "Guide local expert inclus"),
                    T("customWhyTailor3", "Hébergement à votre choix"),
                    T("customWhyTailor4", "Transport privé confortable"),
                    T("customWhyTailor5", "Flexibilité totale"),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Testimonial */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-amber-100/50"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="text-yellow-400 text-lg">★</div>
                  ))}
                </div>
                <p className="text-sm text-gray-700 italic mb-3">
                  "{T("customTestimonialQuote", "Un voyage inoubliable ! L'équipe a su créer un circuit parfaitement adapté à nos envies. Du désert aux montagnes, chaque étape était magique.")}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-sm">
                    SP
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Sophie P.</p>
                    <p className="text-xs text-gray-500">{T("customTestimonialCaption", "Circuit 10 jours • Famille")}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitSurMesure;
